import React, { useCallback, useMemo, useRef, useState } from 'react';
import { MdHelp, MdSearch } from 'react-icons/md';
import { FiUploadCloud } from 'react-icons/fi';
import {
  Badge,
  Box,
  Checkbox,
  HStack,
  Input,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useAuth } from 'contexts/auth';
import { useTurma } from 'contexts/turma';
import api from 'services/api';
import { Table } from './styles';
import ModalMotivoDispensa from '../../Inscrever/ModalMotivoDispensa';

interface IDocumentoTurma {
  id_documento_turma: number;
  nome: string;
  slug: string;
  tipo_upload: string;
  dispensavel: boolean;
  dispensavel_motivo: string | null;
}

interface IProps {
  documentos: IDocumentoTurma[];
  id_turma: number;
  documentosEnviados?: {
    id_documento_turma: number;
    filename: string;
    ext: string;
    dispensado: boolean;
  }[];
  editavel?: boolean;
}

const TableAtualizaAnexos: React.FC<IProps> = ({
  documentos,
  id_turma,
  documentosEnviados = [],
  editavel = false,
}) => {
  const sortedDocs = useMemo(
    () =>
      [...documentos].sort(
        (a, b) => a.id_documento_turma - b.id_documento_turma,
      ),
    [documentos],
  );
  const { user } = useAuth();
  const toast = useToast();
  const {
    adicionaUpload,
    uploads,
    adicionaDispensa,
    removeDispensa,
    removeUpload,
    dispensas,
  } = useTurma();
  const inputFile = useRef<HTMLInputElement[]>([]);
  const [fileNames, setFileNames] = useState<Record<number, string>>({});
  const {
    isOpen: isOpenMotivoDispensa,
    onOpen: onOpenMotivoDispensa,
    onClose: onCloseMotivoDispensa,
  } = useDisclosure();
  const [motivoAtual, setMotivoAtual] = useState('Não especificado');

  const adicionaDocumento = useCallback(
    (index: number, slug: string, id_documento_turma: number) => {
      const files = inputFile.current[index]?.files;

      if (files && files[0] && files[0].size <= 2097152) {
        if (files.length > 0) {
          const file = files[0];

          const parts = file.name.split('.');
          const [ext] = parts.slice(-1);

          const filename = `${id_turma}-${slug}-${user.matricula}`;
          const myNewFile = new File([file], `${filename}.${ext}`, {
            type: file.type,
          });

          const hash_md5 = 'wsdfsd9f876s87f6sdf';
          adicionaUpload({
            file: myNewFile,
            id_documento_turma,
            index,
            filename,
            hash_md5,
            ext,
          });
          setFileNames((prev: Record<number, string>) => ({
            ...prev,
            [index]: `${filename}.${ext}`,
          }));
        }
      } else {
        toast({
          title: 'Tamanho excedido',
          description: 'O arquivo deve possuir tamanho máximo de 2MBytes',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      }
    },
    [user, adicionaUpload, id_turma, toast],
  );

  const onButtonClick = (index: number): void => {
    if (inputFile.current) {
      inputFile.current[index].click();
    }
  };

  const uploadCompleto = useCallback(
    (index: number): boolean => {
      const indexes = uploads.map((up) => up.index);
      return indexes.includes(index);
    },
    [uploads],
  );

  const uploadDesativado = useCallback(
    (index: number): boolean => {
      const indexes = dispensas.map((up) => up.index);
      return indexes.includes(index);
    },
    [dispensas],
  );

  const alteraDispensa = useCallback(
    (value: boolean, index: number, slug: string) => {
      if (value) {
        adicionaDispensa(index, slug);
        removeUpload(index);
        setFileNames((prev: Record<number, string>) => {
          const copy = { ...prev };
          delete copy[index];
          return copy;
        });
      } else {
        removeDispensa(index);
      }
    },
    [adicionaDispensa, removeDispensa, removeUpload],
  );

  const tipoUpload = useCallback((tipo_upload: string): {
    label: string;
    value: string;
  } => {
    switch (tipo_upload) {
      case '1':
        return { label: 'PDF', value: '.pdf' };
      case '2':
        return { label: 'IMAGEM', value: '.jpg, .jpeg, .png' };
      case '3':
        return { label: 'PDF/IMAGE', value: '.jpg, .jpeg, .png, .pdf' };
      default:
        return { label: 'PDF/IMAGE', value: '.jpg, .jpeg, .png, .pdf' };
    }
  }, []);

  const handleClickMotivo = (texto: string | null): void => {
    const motivo = texto || 'Não especificado';
    setMotivoAtual(motivo);
    onOpenMotivoDispensa();
  };

  const verDocumentoServidor = useCallback(
    async (id_documento_turma: number): Promise<void> => {
      const doc = documentosEnviados.find(
        (d) => d.id_documento_turma === id_documento_turma && !d.dispensado,
      );
      if (!doc) return;

      try {
        const response = await api({
          url: `documentos/${doc.filename}.${doc.ext}`,
          method: 'GET',
          responseType: 'blob',
        });

        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: response.data.type }),
        );
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.click();
      } catch (error) {
        toast({
          title: 'Ocorreu um erro.',
          description: 'Não foi possível abrir o documento.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      }
    },
    [documentosEnviados, toast],
  );

  return (
    <Box
      overflowX="auto"
      overflowY="hidden"
      whiteSpace="nowrap"
      maxWidth="100%"
    >
      <Box maxWidth={{ sm: '280px', md: '100%', lg: '100%', xl: '100%' }}>
        <Table>
          <thead>
            <tr>
              <th>#</th>
              <th>Documento</th>
              <th>Dispensado?</th>
              <th className="actions" style={{ textAlign: 'center' }}>
                Upload
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedDocs.map((documento, index) => (
              <tr key={documento.id_documento_turma}>
                <td>{index + 1}</td>
                <td>
                  <span>
                    {documento.nome}{' '}
                    <Badge>{tipoUpload(documento.tipo_upload).label}</Badge>
                  </span>
                </td>
                <td>
                  {documento.dispensavel && editavel ? (
                    <Box as="div" display="flex" alignItems="center">
                      <Checkbox
                        onClick={(e: any) =>
                          alteraDispensa(
                            e.target.checked,
                            index,
                            documento.slug,
                          )
                        }
                      >
                        Sim
                      </Checkbox>
                      <MdHelp
                        size={20}
                        color="#3182ce"
                        cursor="pointer"
                        onClick={() =>
                          handleClickMotivo(documento.dispensavel_motivo)
                        }
                      />
                    </Box>
                  ) : null}
                </td>
                <td className="actions">
                  <HStack>
                    {editavel && (
                      <Input
                        type="file"
                        id={`file-atualiza-${index}`}
                        name={documento.slug}
                        ref={(el: HTMLInputElement | null) => {
                          if (el) {
                            inputFile.current[index] = el;
                          }
                        }}
                        accept={tipoUpload(documento.tipo_upload).value}
                        size="sm"
                        style={{ display: 'none' }}
                        onChange={() =>
                          adicionaDocumento(
                            index,
                            documento.slug,
                            documento.id_documento_turma,
                          )
                        }
                      />
                    )}
                    {uploadCompleto(index) ? (
                      <Tooltip
                        label="Visualizar arquivo selecionado"
                        hasArrow
                        placement="left"
                      >
                        <Box>
                          <MdSearch
                            size={20}
                            color="green"
                            cursor="pointer"
                            onClick={() => {
                              const files = inputFile.current[index]?.files;
                              if (files && files[0]) {
                                const url = window.URL.createObjectURL(
                                  files[0],
                                );
                                window.open(url);
                              }
                            }}
                          />
                        </Box>
                      </Tooltip>
                    ) : (
                      documentosEnviados.some(
                        (d) =>
                          d.id_documento_turma ===
                            documento.id_documento_turma && !d.dispensado,
                      ) && (
                        <Tooltip label="Ver Arquivo" hasArrow placement="left">
                          <Box>
                            <MdSearch
                              size={20}
                              color="#3182ce"
                              cursor="pointer"
                              onClick={() =>
                                verDocumentoServidor(
                                  documento.id_documento_turma,
                                )
                              }
                            />
                          </Box>
                        </Tooltip>
                      )
                    )}
                    {editavel && (
                      <Tooltip label="Enviar Arquivo" hasArrow placement="left">
                        <Box>
                          <FiUploadCloud
                            size={20}
                            color={
                              uploadCompleto(index) && !uploadDesativado(index)
                                ? 'green'
                                : 'black'
                            }
                            onClick={() => {
                              if (!uploadDesativado(index)) {
                                onButtonClick(index);
                              }
                            }}
                            cursor="pointer"
                          />
                        </Box>
                      </Tooltip>
                    )}
                    {editavel && uploadCompleto(index) && fileNames[index] && (
                      <Text
                        fontSize="sm"
                        color="gray.600"
                        maxW="120px"
                        isTruncated
                      >
                        {fileNames[index]}
                      </Text>
                    )}
                  </HStack>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <ModalMotivoDispensa
          isOpen={isOpenMotivoDispensa}
          onClose={onCloseMotivoDispensa}
          texto={motivoAtual}
        />
      </Box>
    </Box>
  );
};

export default TableAtualizaAnexos;
